<section class="go-container">
  <app-form-control-docs class="go-column go-column--100"></app-form-control-docs>

  <go-card id="form-input-control" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Control</h2>
      <go-copy cardId="form-input-control" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          As with most of the form components, the only <code class="code-block--inline">@Input</code>
          required of the input component is a <code class="code-block--inline">FormControl</code>.
          The FormControl can be passed in via the <code class="code-block--inline">[control]</code>
          attribute on the &lt;go-input&gt; component.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input [control]="name"></go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicInputExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-label" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Label</h2>
      <go-copy cardId="form-input-label" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          All form components include an <code class="code-block--inline">@Input() label: string;</code>
          that can be used to add a label to the input components.
          In addition to displaying an HTML label, the text passed
          in via the <code class="code-block--inline">[label]</code>
          attribute is used to generate a unique ID to associate the
          label with the input. If no label is passed in, a generic,
          but still unique ID will be generated.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name2"
              label="Your Name">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicInputLabelExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-key" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Key</h2>
      <go-copy cardId="form-input-key" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          As stated above the label attribute is used to generate a
          unique ID to associate the label with the input. As this might
          not be desired, the <code class="code-block--inline">@Input() key: string;</code>
          can be used to configure the ID attribute of the input directly.
          Anything passed into the component via the <code class="code-block--inline">[key]</code>
          attribute will be used to both assign the ID to the input and
          associate the label with the input.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name3"
              label="Your Name"
              key="your-name-input">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicInputKeyExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
          <div class="go-column go-column--100">
            <h4 class="go-heading-4 go-heading--underlined">Example Output</h4>
            <p class="go-body-copy">
              Notice in the below example output that the <code class="code-block--inline">key</code> has been assigned
              to both the <code class="code-block--inline">id</code> attribute on
              the input and the <code class="code-block--inline">for</code> attribute
              on the label.
            </p>
            <code
              [highlight]="basicInputKeyExampleOutput"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-hints" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Hints</h2>
      <go-copy cardId="form-input-hints" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          Form hints exist to help give more information about the
          inputs they are attached to. The <code class="code-block--inline">@Input() key: Array&lt;string&gt;</code>
          can be used to pass in an array of hints to the input component.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name4"
              label="Your Name"
              [hints]="hints">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicHintsTemplate"
              class="code-block--no-bottom-margin">
            </code>
            <code
              [highlight]="basicInputHintsExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-errors" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Errors</h2>
      <go-copy cardId="form-input-errors" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          FormControls all have a <a class="go-link" href="https://angular.io/api/forms/AbstractControl#setErrors">consistent way of setting errors</a>
          via the <code class="code-block--inline">setErrors()</code> function.
          All goponents use this API to display errors on our components, but the
          data needs to be displayed in a specific structure.
        </p>
        <p class="go-body-copy go-body-copy--no-margin">
          By default the input type is set to "Error", however this can be
          overridden as shown below.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name5"
              label="Your Name">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicInputLabelExample"
              class="code-block--no-bottom-margin">
            </code>
            <code
              [highlight]="basicErrorsTemplate"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-disabled" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Disable Component</h2>
      <go-copy cardId="form-input-disabled" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          FormControls can be either created as disable or later disabled
          and re-enabled directly after they are created.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <div class="go-container">
              <div class="go-column go-column--100">
                <go-input
                  [control]="name6"
                  label="First Name">
                </go-input>
              </div>
              <div class="go-column go-column--100">
                <go-input
                  [control]="name7"
                  label="Last Name">
                </go-input>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicDisabledExample"
              class="code-block--no-bottom-margin">
            </code>
            <code
              [highlight]="basicDisabledExample2"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-placeholder" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Placeholder</h2>
      <go-copy cardId="form-input-placeholder" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          The input component's placeholder attribute can be set via
          <code class="code-block--inline">@Input() placeholder: string;</code>
          and the <code class="code-block--inline">[placeholder]</code> attribute.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name8"
              label="Your Name"
              placeholder="Nymphadora Tonks">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicPlaceholderExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-type" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Type</h2>
      <go-copy cardId="form-input-type" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          The input type can be set via the
          <code class="code-block--inline">@Input() inputType: string;</code>
          and the <code class="code-block--inline">[inputType]</code> attribute.
          By default this is set to text, but it should be changed to implement
          other basic text based input types.
        </p>
        <p class="go-body-copy go-body-copy--no-margin">
          In the example below we show how to use <code class="code-block--inline">[inputType]</code>
          to build a password input.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="password"
              label="Your Password"
              inputType="password">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicPasswordExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>

  <go-card id="form-input-max-min-length" class="go-column go-column--100">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Component Maxlength And Minlength</h2>
      <go-copy cardId="form-input-max-min-length" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          The maxlength and minlength properties of the input can be set via
          <code class="code-block--inline">@Input() maxlength: number;</code>
          and the <code class="code-block--inline">@Input() minlength: number;</code> bindings.
          By default the <code class="code-block--inline">maxlength</code> is set to 
          <code class="code-block--inline">524288</code> and the 
          <code class="code-block--inline">minlength</code> is set to <code class="code-block--inline">0</code>.
          Please note that the <code class="code-block--inline">minlength</code> property would come into
          effect when the corresponding form containing the input component would be submitted.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <div class="go-container">
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-input
              [control]="name"
              label="Name"
              maxlength="10"
              minlength="5">
            </go-input>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <code
              [highlight]="basicLengthExample"
              class="code-block--no-bottom-margin">
            </code>
          </div>
        </div>
      </div>

    </div>
  </go-card>
</section>
